import type { ReactNode } from "react";
import {
  Image,
  Platform,
  Pressable,
  Text,
  View,
  type ImageSourcePropType,
  type ImageStyle,
  type StyleProp,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "../../../onboarding/theme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type AccessGridCardProps = {
  title: string;
  description?: string;
  imageSource?: ImageSourcePropType;
  imageStyle?: StyleProp<ImageStyle>;
  fallbackVisual?: ReactNode;
  tags?: ReactNode;
  onPress: () => void;
  /** Stable analytics identifier for the grid this card belongs to. */
  action?: string;
};

export function AccessGridCardPill({
  label,
  icon: Icon,
}: {
  label: string;
  icon?: LucideIcon;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 8,
        backgroundColor: "rgba(0,0,0,0.03)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.05)",
      }}
    >
      {Icon ? (
        <Icon size={11} color={EMBER.colors.textMid} strokeWidth={1.8} />
      ) : null}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          fontWeight: "500",
          color: EMBER.colors.textMid,
          letterSpacing: 0,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

export default function AccessGridCard({
  title,
  description,
  imageSource,
  imageStyle,
  fallbackVisual,
  tags,
  onPress,
  action = "access_grid_card",
}: AccessGridCardProps) {
  const capturePress = usePressCapture("AccessGridCard");
  return (
    <Pressable
      onPress={() => {
        // Card titles are authored catalogue copy, not patient data.
        capturePress(pressLabel(title), action);
        onPress();
      }}
      style={
        {
          flex: 1,
          borderRadius: 22,
          overflow: "hidden",
          backgroundColor: "rgba(255, 255, 255, 0.4)",
          borderWidth: 1,
          borderColor: "rgba(255, 255, 255, 0.6)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.04,
          shadowRadius: 16,
          ...(Platform.OS === "web" && {
            backdropFilter: "blur(40px)",
            WebkitBackdropFilter: "blur(40px)",
          }),
        } as Record<string, unknown>
      }
    >
      <View
        style={{
          alignItems: "center",
          paddingTop: 24,
          paddingBottom: 12,
        }}
      >
        {imageSource ? (
          <Image source={imageSource} style={imageStyle} resizeMode="contain" />
        ) : (
          fallbackVisual
        )}
      </View>

      <View
        style={{
          flex: 1,
          padding: 16,
          paddingTop: 4,
          gap: 6,
          alignItems: "center",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: "700",
            color: EMBER.colors.text,
            lineHeight: 20,
            textAlign: "center",
          }}
          numberOfLines={2}
        >
          {title}
        </Text>

        {description ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              lineHeight: 17,
              textAlign: "center",
            }}
            numberOfLines={2}
          >
            {description}
          </Text>
        ) : null}

        <View style={{ flex: 1 }} />

        {tags ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 4,
              marginTop: 4,
            }}
          >
            {tags}
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}
